import { useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';

export type RealtimeStatus = 'SUBSCRIBED' | 'TIMED_OUT' | 'CLOSED' | 'CHANNEL_ERROR' | 'CONNECTING';

export function useRealtime(
  schoolProfileId?: string,
  token?: string | null,
  _onEvent?: () => void,
  _options?: { debug?: boolean; debounceMs?: number }
) {
  const [status, setStatus] = useState<RealtimeStatus>('CONNECTING');
  const [lastEventTime, setLastEventTime] = useState<Date | null>(null);
  const channelRef = useRef<RealtimeChannel | null>(null);

  useEffect(() => {
    if (!schoolProfileId || !token) {
      setStatus('CLOSED');
      return;
    }

    setStatus('CONNECTING');

    const channelName = `sync-${schoolProfileId}`;

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public' },
        () => {
          setLastEventTime(new Date());
        }
      )
      .subscribe((newStatus) => {
        setStatus(newStatus as RealtimeStatus);
      });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [schoolProfileId, token]);

  return {
    status,
    isConnected: status === 'SUBSCRIBED',
    lastEventTime,
  };
}

export default useRealtime;
